import {
  adaptCategoryRows,
  adaptExtendedThresholdRows,
  adaptListRows,
  collectVitalKeys,
} from "./adaptReport";
import {
  generateCategoryReportPDF,
  generateExtendedThresholdReportPDF,
  generateListReportPDF,
} from "./exportReport";
import { REPORT_VIEWS, formatReportNumber } from "./reportViews";

// "2026-06-05" -> "Jun 5, 2026" (same label the live report header uses).
const fmtYmd = (ymd) => {
  if (!ymd || typeof ymd !== "string") return "";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const nameOf = (value) =>
  value && typeof value === "object" ? value.name || "" : value || "";

/**
 * Rebuild a report's PDF from its detail response (GET reports/{id}/) as a
 * Blob. PDFs aren't stored, but every report keeps a frozen snapshot, so the
 * rows always match what was generated (removed patients are already
 * excluded by the detail endpoint). Header values come from the report and
 * the history item, never from the report screen's current filters.
 * Returns { blob, filename }.
 */
export const buildReportPdfFromDetail = (detail, historyItem = {}) => {
  const view = detail?.report_view || historyItem.report_view;
  const snapshot = detail?.snapshot || {};
  const filters = { ...(snapshot.filters || {}), ...(detail?.filters || {}) };
  // History is always for the current account; its items carry no account name.
  const accountName =
    [sessionStorage.getItem("acc_name") || "", nameOf(historyItem.location)]
      .filter(Boolean)
      .join(", ") || "—";

  if (view === REPORT_VIEWS.EXTENDED_THRESHOLD) {
    // The snapshot's own filters hold the cycle dates (end = submission date).
    const etFilters = snapshot.filters || filters;
    return generateExtendedThresholdReportPDF({
      accountName,
      reportNumberDisplay: formatReportNumber(
        detail?.report_number ?? snapshot.report_number,
        detail?.report_number_display || snapshot.report_number_display
      ),
      startDate: etFilters.start_date,
      endDate: etFilters.end_date,
      thresholdKey: snapshot.threshold_key,
      highlightKey: snapshot.highlight_key,
      rows: adaptExtendedThresholdRows(snapshot),
      asBlob: true,
    });
  }

  const common = {
    accountName,
    dateRange: `${fmtYmd(filters.start_date)} - ${fmtYmd(filters.end_date)}`,
    periodDate: filters.end_date,
    providerLabel: nameOf(historyItem.provider) || "All Providers",
    asBlob: true,
  };
  if (view === REPORT_VIEWS.LIST) {
    const listRows = adaptListRows(snapshot);
    return generateListReportPDF({ ...common, vitalKeys: collectVitalKeys(listRows), listRows });
  }
  const rows = adaptCategoryRows(snapshot);
  return generateCategoryReportPDF({ ...common, vitalKeys: collectVitalKeys(rows), rows });
};
